import React, { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./PopupConfig.css";
import { CONFIG } from "../../config";

/** ---- Config ---- */
const UPLOAD_URL = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;

/** ---- Helpers ---- */
const imgURL = (name) => (name ? `${IMAGES_BASE}/${encodeURIComponent(name)}` : "");

export default function PopupConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [image, setImage] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const [showContactForm, setShowContactForm] = useState(false);
  const [useImageAsBackground, setUseImageAsBackground] = useState(false);

  /** NEW — title & message color */
  const [titleColor, setTitleColor] = useState("#111827");
  const [messageColor, setMessageColor] = useState("#4b5563");

  /** Trigger options */
  const [enablePopup, setEnablePopup] = useState(true);
  const [triggerOnVisit, setTriggerOnVisit] = useState(true);
  const [triggerOnRoute, setTriggerOnRoute] = useState(false);
  const [intervalEnabled, setIntervalEnabled] = useState(false);
  const [intervalSeconds, setIntervalSeconds] = useState(60);

  /** Load existing config on mount */
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const snap = await get(ref(db, "popupConfig"));
        if (snap.exists()) {
          const data = snap.val();

          setImage(data.image || "");
          setTitle(data.title || "");
          setMessage(data.message || "");
          setShowContactForm(!!data.showContactForm);
          setUseImageAsBackground(!!data.useImageAsBackground);

          setTitleColor(data.titleColor || "#111827");
          setMessageColor(data.messageColor || "#4b5563");

          if (data.triggers) {
            const t = data.triggers;
            setEnablePopup(t.enable !== false);
            setTriggerOnVisit(!!t.onVisit);
            setTriggerOnRoute(!!t.onRouteChange);
            setIntervalEnabled(!!t.intervalEnabled);
            setIntervalSeconds(
              typeof t.intervalSeconds === "number" && !isNaN(t.intervalSeconds)
                ? t.intervalSeconds
                : 60
            );
          }
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load popup config.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  /** Handle image upload */
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setSuccess("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(UPLOAD_URL, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      const data = await res.json();
      if (!data.filename) throw new Error("Invalid upload response");

      setImage(data.filename);
      setSuccess("Image uploaded successfully.");
    } catch (err) {
      console.error(err);
      setError("Image upload failed. Please try again.");
    }
  };

  /** Save to Firebase */
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await set(ref(db, "popupConfig"), {
        image,
        title: title.trim(),
        message: message.trim(),
        showContactForm: !!showContactForm,
        useImageAsBackground: !!useImageAsBackground,
        titleColor,
        messageColor,
        triggers: {
          enable: !!enablePopup,
          onVisit: !!triggerOnVisit,
          onRouteChange: !!triggerOnRoute,
          intervalEnabled: !!intervalEnabled,
          intervalSeconds:
            intervalEnabled ? Number(intervalSeconds) || 0 : 0,
        },
      });
      setSuccess("Popup settings saved successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to save popup settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="pc-wrap">Loading popup config…</div>;

  return (
    <div className="pc-wrap">
      <h1 className="pc-title">Popup Configuration</h1>

      {error && <div className="pc-alert pc-alert-error">{error}</div>}
      {success && <div className="pc-alert pc-alert-success">{success}</div>}

      <form className="pc-form" onSubmit={handleSave}>
        {/* Image */}
        <div className="pc-field">
          <label className="pc-label">Popup Image</label>
          <div className="pc-image-row">
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            {image && (
              <div className="pc-image-preview">
                <img src={imgURL(image)} alt="Popup" />
                <button
                  type="button"
                  className="pc-btn pc-btn-text"
                  onClick={() => setImage("")}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          <p className="pc-hint">
            Recommended: JPG/PNG, reasonable size. Current: {image || "none"}
          </p>
        </div>

        {/* Use image as background */}
        <div className="pc-field pc-toggle-row">
          <label className="pc-label">
            Use Image as Background?
            <div className="pc-hint">
              Enable this to set the uploaded image as the popup&apos;s full background.
            </div>
          </label>
          <label className="pc-switch">
            <input
              type="checkbox"
              checked={useImageAsBackground}
              onChange={(e) => setUseImageAsBackground(e.target.checked)}
            />
            <span className="pc-slider" />
          </label>
          <span className="pc-toggle-text">
            {useImageAsBackground ? "Background Mode" : "Normal Mode"}
          </span>
        </div>

        {/* Title */}
        <div className="pc-field">
          <label className="pc-label">Title</label>
          <input
            className="pc-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Get a Free Design Consultation"
          />
        </div>

        {/* Title color */}
        <div className="pc-field">
          <label className="pc-label">Title Color</label>
          <input
            type="color"
            value={titleColor}
            onChange={(e) => setTitleColor(e.target.value)}
          />
        </div>

        {/* Message */}
        <div className="pc-field">
          <label className="pc-label">Message / Description</label>
          <textarea
            className="pc-textarea"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write the content that should appear in the popup…"
          />
        </div>

        {/* Message color */}
        <div className="pc-field">
          <label className="pc-label">Message Color</label>
          <input
            type="color"
            value={messageColor}
            onChange={(e) => setMessageColor(e.target.value)}
          />
        </div>

        {/* Toggle: Show contact form */}
        <div className="pc-field pc-toggle-row">
          <label className="pc-label">
            Include Contact Form inside Popup?
            <div className="pc-hint">
              When enabled, your existing contact form will appear inside the popup.
            </div>
          </label>
          <label className="pc-switch">
            <input
              type="checkbox"
              checked={showContactForm}
              onChange={(e) => setShowContactForm(e.target.checked)}
            />
            <span className="pc-slider" />
          </label>
          <span className="pc-toggle-text">
            {showContactForm ? "Content + Form" : "Only Content"}
          </span>
        </div>

        {/* Triggers */}
        <div className="pc-field">
          <label className="pc-label">When should the popup be shown?</label>

          {/* Master enable/disable */}
          <div className="pc-toggle-row" style={{ marginBottom: 8 }}>
            <span className="pc-toggle-text">Enable popup?</span>
            <label className="pc-switch">
              <input
                type="checkbox"
                checked={enablePopup}
                onChange={(e) => setEnablePopup(e.target.checked)}
              />
              <span className="pc-slider" />
            </label>
            <span className="pc-toggle-text">
              {enablePopup ? "Enabled" : "Disabled (None)"}
            </span>
          </div>

          {!enablePopup && (
            <p className="pc-hint">
              Popup is disabled. Turn it on to choose when it should appear.
            </p>
          )}

          {enablePopup && (
            <>
              <div className="pc-check-group">
                <label className="pc-check-item">
                  <input
                    type="checkbox"
                    checked={triggerOnVisit}
                    onChange={(e) => setTriggerOnVisit(e.target.checked)}
                  />
                  <span>When user visits the site (first load / session)</span>
                </label>

                <label className="pc-check-item">
                  <input
                    type="checkbox"
                    checked={triggerOnRoute}
                    onChange={(e) => setTriggerOnRoute(e.target.checked)}
                  />
                  <span>On every new page of this website</span>
                </label>

                <label className="pc-check-item">
                  <input
                    type="checkbox"
                    checked={intervalEnabled}
                    onChange={(e) => setIntervalEnabled(e.target.checked)}
                  />
                  <span>Every N seconds (enter seconds below)</span>
                </label>
              </div>

              {intervalEnabled && (
                <div className="pc-interval-row">
                  <input
                    type="number"
                    min="5"
                    className="pc-input pc-input-inline"
                    value={intervalSeconds}
                    onChange={(e) => setIntervalSeconds(e.target.value)}
                    placeholder="e.g. 30"
                  />
                  <span className="pc-interval-label">seconds</span>
                </div>
              )}

              <p className="pc-hint">
                You can select multiple options. Example: first visit + every new page + every 30 seconds.
              </p>
            </>
          )}
        </div>

        <div className="pc-actions">
          <button className="pc-btn pc-btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
